import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  ShieldCheck,
  Activity,
  CheckCircle2,
  Hash,
  BadgeCheck,
} from "lucide-react";
import BrandName from "@/components/site/BrandName";
import { Monogram } from "@/components/site/Brand";
import AidoidoMark from "@/components/site/AidoidoMark";
import { artwork } from "@/lib/brand-artwork";

const capabilities = [
  ["Developers", "In-house engineering"],
  ["Product design", "Interface & UX"],
  ["Admin & support", "Live human help desk"],
  ["Legal & compliance", "Regulatory oversight"],
  ["Call centre", "Patient acquisition"],
  ["Community", "Advocacy & feedback"],
];

/* Small subtle product mock — the About hero visual */
function AboutProductMock() {
  return (
    <div className="relative rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#0B1428] to-[#050B18] p-4 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] overflow-hidden">
      <div className="pointer-events-none absolute -top-20 -right-20 h-52 w-52 rounded-full bg-emerald-500/[0.12] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-52 w-52 rounded-full bg-[#D4A65A]/[0.1] blur-3xl" />

      <div className="relative">
        {/* window chrome */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-white/10" />
            <span className="h-2 w-2 rounded-full bg-white/10" />
            <span className="h-2 w-2 rounded-full bg-white/10" />
          </div>
          <div className="inline-flex items-center gap-1.5 text-[9.5px] font-mono text-slate-500">
            <Activity size={10} className="text-emerald-300" />
            cannicomply.app / compliance
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            live
          </span>
        </div>

        {/* metric row */}
        <div className="grid grid-cols-3 gap-2.5 mb-3">
          {[
            // [label, value, unit?] — unit rendered as a separate emerald-300
            // span to match PainPoints.GoodMetricsGrid. Certificates 42/42 is
            // a fraction, not a rate, so no unit is passed and no unit span
            // renders — value shows as-is.
            ["Acknowledgement", "98", "%"],
            ["Comprehension", "92", "%"],
            ["Certificates", "42/42"],
          ].map(([l, v, u]) => (
            <div
              key={l}
              className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5"
            >
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-mono">
                {l}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-white text-lg font-semibold tracking-tight">
                  {v}
                </span>
                {u && (
                  <span className="text-emerald-300 text-[11px] font-mono">
                    {u}
                  </span>
                )}
              </div>
              <div className="mt-2 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                <div className="h-full w-11/12 bg-gradient-to-r from-emerald-400/80 to-emerald-300" />
              </div>
            </div>
          ))}
        </div>

        {/* event feed */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4A65A] font-mono">
              Live compliance feed
            </div>
            <span className="text-[9px] font-mono text-slate-500">Today</span>
          </div>
          {[
            ["gold", "SOP-CB-14 distributed to 42 staff", "09:14"],
            ["emerald", "R. Ahmed acknowledged \u00b7 96%", "10:24"],
            ["emerald", "Certificate #A7F2-91C minted", "10:26"],
            ["emerald", "Chained to audit trail", "10:26"],
          ].map(([tone, text, t], i) => (
            <div
              key={i}
              className="flex items-center gap-2 py-1.5 border-b border-white/[0.03] last:border-b-0"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  tone === "emerald" ? "bg-emerald-400" : "bg-[#D4A65A]"
                }`}
              />
              <span className="text-[11px] text-slate-300 flex-1 min-w-0 truncate">
                {text}
              </span>
              <span className="text-[9.5px] font-mono text-slate-600">{t}</span>
            </div>
          ))}
        </div>

        {/* footer chip strip */}
        <div className="mt-3 flex items-center gap-2 text-[10px] font-mono text-slate-500">
          <span className="inline-flex items-center gap-1 text-emerald-300">
            <Hash size={10} /> SHA-256
          </span>
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1">
            <BadgeCheck size={10} className="text-[#D4A65A]" /> signed
          </span>
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1">
            <ShieldCheck size={10} className="text-emerald-300" /> retained 7y
          </span>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-14 md:py-24 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-30" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="eyebrow">Behind the platform</div>
            <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
              Ten years of listening.<br />
              <span className="text-[#D4A65A]">Now, software.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-slate-400 leading-relaxed text-[15px] max-w-md">
            <BrandName /> is built by UKCSL, the UK Cannabis Support Line. A
            network of professionals we have grown over a decade. Developers,
            designers, admin, support, legal and compliance. Everything in
            house.
          </p>
        </div>

        {/* Product UI hero — dominant visual. Photo is a small secondary card. */}
        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8"
          >
            <AboutProductMock />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-4 flex flex-col gap-4"
          >
            {/* UKCSL brand banner.

                The source image is a wide 2:1 landscape (1774×887).
                We match the container's aspect ratio to the image
                (aspect-[2/1]) so `object-cover` doesn't crop the sides
                — which was chopping "Building technology and community
                for the regulated cannabis industry" off on the left.
                No flex-1 / min-h — the aspect ratio drives the height. */}
            <div
              data-testid="about-ukcsl-banner"
              className="relative isolate rounded-xl overflow-hidden border border-white/[0.08] bg-black w-full aspect-[2/1]"
            >
              <img
                src={artwork.ukcslBanner}
                alt="UKCSL — Building technology and community for the regulated cannabis industry. CanniComply compliance software, Aidoido technology, UNIT CSC call centre, patient acquisition, community support."
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B18]/60 via-transparent to-[#050B18]/25 pointer-events-none" />
              <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[9.5px] uppercase tracking-[0.22em] text-[#D4A65A]">
                <span className="h-1 w-4 bg-[#D4A65A]/70" />
                Behind the platform
              </div>
            </div>

            {/* Trust chip card — flex-1 so the right column matches the
                left product mock height without stretching the banner
                image out of its natural 2:1 aspect ratio. */}
            <div className="rounded-xl border border-white/[0.06] bg-[#070D1C] p-4 flex-1 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-mono mb-2">
                Shaped by real operators
              </div>
              <p className="text-[12.5px] text-slate-400 leading-relaxed">
                Every feature is built from conversations with clinics,
                pharmacies and manufacturers we work with every day.
              </p>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-300">
                <CheckCircle2 size={12} /> Not a stock template
              </div>
            </div>
          </motion.div>
        </div>

        {/* Capability grid */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/[0.05] rounded-lg overflow-hidden border border-white/[0.05]">
          {capabilities.map(([a, b]) => (
            <div
              key={a}
              className="bg-[#070D1C] hover:bg-[#0A1428] transition-colors p-5"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4A65A]">
                {a}
              </div>
              <div className="mt-1.5 text-[12px] text-slate-400 leading-snug">
                {b}
              </div>
            </div>
          ))}
        </div>

        {/* Founder voice + Aidoido partner card */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-7 relative overflow-hidden rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] p-8 md:p-10">
            <div className="text-[#D4A65A]/25 font-display text-6xl leading-none">
              &ldquo;
            </div>
            <p className="-mt-4 text-white text-[19px] md:text-[22px] font-semibold tracking-[-0.015em] leading-[1.35] max-w-2xl">
              A decade supporting UK medical cannabis patients has taught us
              every operational gap in this industry. UKCSL, Aidoido and{" "}
              <BrandName /> are the working answer. Built by professionals,
              for the professionals who run regulated cannabis operations.
            </p>
            <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-4">
                <Monogram initials="WD" size={44} />
                <div>
                  <div className="text-white text-[15px] font-semibold tracking-tight">
                    Wayne Doyle
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
                    Founder, UKCSL
                  </div>
                </div>
              </div>
              <div className="md:ml-auto flex flex-col gap-1.5">
                <a
                  href="tel:01615241567"
                  className="inline-flex items-center gap-2 text-[12px] text-slate-300 hover:text-white transition-colors"
                >
                  <Phone size={12} className="text-[#D4A65A]" /> 0161 524 1567
                </a>
                <a
                  href="mailto:wayne@ukcsl.co.uk"
                  className="inline-flex items-center gap-2 text-[12px] text-slate-300 hover:text-white transition-colors"
                >
                  <Mail size={12} className="text-[#D4A65A]" /> wayne@ukcsl.co.uk
                </a>
              </div>
            </div>
          </div>

          <a
            href="https://www.aidoido.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-aidoido-card"
            className="lg:col-span-5 group relative overflow-hidden rounded-xl border border-white/[0.08] hover:border-white/25 bg-gradient-to-br from-[#0C1428] to-[#050B18] p-8 flex flex-col justify-between transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#22D3EE]/15 to-[#F472B6]/15 blur-xl" />
                <AidoidoMark size={80} className="relative" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 group-hover:text-white transition-colors inline-flex items-center gap-1.5">
                Technology partner
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="opacity-40 group-hover:opacity-100 transition-opacity"
                >
                  <path d="M7 17 17 7M8 7h9v9" />
                </svg>
              </span>
            </div>

            <div>
              <h3 className="mt-8 font-display text-2xl md:text-3xl text-white tracking-[-0.025em] leading-[1.1] font-semibold">
                Aidoido builds the software.
              </h3>
              <p className="mt-3 text-slate-400 text-[13px] leading-relaxed max-w-sm">
                AI powered digital growth. Product engineering, automation and
                user experience for regulated markets. UNIT CSC and{" "}
                <BrandName /> are shipped from the same team.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Product", "AI automation", "Security", "Reliability"].map(
                  (c) => (
                    <span
                      key={c}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] text-slate-300"
                    >
                      {c}
                    </span>
                  )
                )}
              </div>
            </div>
          </a>
        </div>

        {/* Team roll */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { initials: "WD", name: "Wayne Doyle", role: "Founder, UKCSL", tone: "#D4A65A" },
            { initials: "AI", name: "Aidoido", role: "Technology partner", tone: "#7DD3FC" },
            { initials: "L&C", name: "Legal & Compliance", role: "In house", tone: "#D4A65A" },
            { initials: "CC", name: "Call Centre", role: "Patient acquisition", tone: "#D4A65A" },
          ].map((p) => (
            <div
              key={p.name}
              className="rounded-xl border border-white/[0.06] bg-[#070D1C] p-5"
            >
              <Monogram initials={p.initials} tone={p.tone} size={40} />
              <div className="mt-4 text-white text-[14px] font-semibold tracking-tight">
                {p.name}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1">
                {p.role}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
